import { useMemo } from 'react';

import { useSearchParameters } from '@/components/signup/hooks/use-search-parameters';
import { JiraFY27H1HoldoutExclusionTrigger } from '@/components/signup/jira-fy27h1-holdout-exclusion-trigger/jira-fy27h1-holdout-exclusion-trigger';
import { ProvisioningFunnelUfoProvider } from '@/components/signup/provisioning-funnel-ufo/provisioning-funnel-ufo';
import { ProductKey } from '@/components/signup/utils/products';
import { usePrefersDarkColorScheme } from '@/components/user-context/helpers/cohorts';
import { checkFeatureFlagGate } from '@/utils/feature-flags/check-feature-flag';
import { featureFlags } from '@/utils/feature-flags/feature-flag-manifest';

import { AnalyticsClientUpdater } from '../../components/analytics-client-updater';
import { AutoSelectThemePreference } from '../../components/auto-select-theme-preference';
import { useCloudStatus } from '../../hooks/use-cloud-status';
import { useCloudStatusRedirection } from '../../hooks/use-cloud-status-redirection';
import { TenantAwareStatsigClientUpdaterProvider } from '../../hooks/use-tenant-aware-statsig-client-updater';
import { PerceivedProvisioningStartTimer } from '../../utils/perceived-provisioning/perceived-provisioning-start-timer';
import { jiraScreenConfig } from '../../utils/screen-manager/jira-screen-config';
import { jiraScreenDictionary } from '../../utils/screen-manager/jira-screen-dictionary';
import { DeferSiteNamingGate } from '../jira/defer-site-naming-gate/defer-site-naming-gate';
import { JiraAnimatedProvisioningScreen } from '../jira/jira-animated-provisioning-screen/jira-animated-provisioning-screen';
import { ProductRedirector } from '../product-redirector/product-redirector';

import { BaseProvisioningUI } from './base-provisioning-ui';

/**
 * Component that shows provisioning UI for Jira.
 * @returns @type {JSX.Element}
 */
const ProvisioningContent = () => {
	const { cloudId } = useCloudStatus();
	const { signupSource } = useSearchParameters();

	// DeferSiteNamingGate (D1D6-47, ATLAS-126008) intercepts the render when
	// the user is in the deferred variant — it shows the deferred loading
	// view while `useDeferSiteActivation` runs, then rewrites the URL via
	// `history.replaceState` and falls through to the normal Jira flow.
	// For 100% of current prod traffic (no `?deferSiteNaming=true`) this is
	// a transparent passthrough.
	return (
		<DeferSiteNamingGate>
			<>
				<AutoSelectThemePreferenceWrapper />
				<AnalyticsClientUpdater />
				{signupSource === 'jira-ai-native' && (
					<JiraFY27H1HoldoutExclusionTrigger cloudId={cloudId} />
				)}
				<BaseProvisioningUI
					screenConfig={jiraScreenConfig}
					screenDictionary={jiraScreenDictionary}
					screenFallback={
						<PerceivedProvisioningStartTimer>
							<ProductRedirector>
								<JiraAnimatedProvisioningScreen />
							</ProductRedirector>
						</PerceivedProvisioningStartTimer>
					}
				/>
			</>
		</DeferSiteNamingGate>
	);
};

export const JiraProvisioningUI = () => {
	const { addBlockingRedirectPromise } = useCloudStatusRedirection();
	const isProvisioningFunnelUfoEnabled = checkFeatureFlagGate(
		featureFlags.provisioningFunnelUfoImprovements,
	);

	return (
		<TenantAwareStatsigClientUpdaterProvider>
			{isProvisioningFunnelUfoEnabled ? (
				<ProvisioningFunnelUfoProvider
					initialCustomData={{ productKey: ProductKey.JIRA_SOFTWARE }}
					addBlockingRedirectPromise={addBlockingRedirectPromise}
				>
					<ProvisioningContent />
				</ProvisioningFunnelUfoProvider>
			) : (
				<ProvisioningContent />
			)}
		</TenantAwareStatsigClientUpdaterProvider>
	);
};

const AutoSelectThemePreferenceWrapper = () => {
	const { cloudId, createCloudStatus } = useCloudStatus();
	const { addBlockingRedirectPromise } = useCloudStatusRedirection();
	const prefersDarkColorScheme = usePrefersDarkColorScheme();

	const preconditionsMet = useMemo(
		() => cloudId && createCloudStatus === 'success',
		[cloudId, createCloudStatus],
	);

	// Users whose browser prefers a dark color scheme have their Jira theme set
	// to "auto" so the site matches their system appearance from first load.
	// preconditionsMet ensures the site is ready before we hit the Jira theme
	// preference API. Otherwise, it will throw an error if the tenant is not ready.
	return preconditionsMet && prefersDarkColorScheme ? (
		<AutoSelectThemePreference
			cloudId={cloudId}
			product="jira"
			onRequest={addBlockingRedirectPromise}
		/>
	) : null;
};
